import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useTheme } from 'styled-components'
import { motion } from 'framer-motion'
import { StaticImage } from 'gatsby-plugin-image'

import { useScrollTrigger } from '../hooks/useScrollTrigger'

import { Container } from '../styles/PageLayout'
import { TitleM, TitleXXS, BodyM, BodyXS } from '../styles/Typography'
import { KnxToCrestronStepsStyled } from '../styles/components/KnxToCrestronStepsStyled'

import GridBackground from './icons/grid-background'

/**
 * The Knx to Crestron Home steps block.
 * @param {String | Array} props.badge
 * @param {String | Array} props.subtitle
 * @param {Node} props.link
 * @returns {FunctionComponent}
 */
const KnxToCrestronSteps = ({ badge, subtitle, link }) => {
  const intl = useIntl()
  const theme = useTheme()

  const hasReachedContent1 = useScrollTrigger('content-1', 100)
  const hasReachedContent2 = useScrollTrigger('content-2', 100)
  const hasReachedContent3 = useScrollTrigger('content-3', 100)
  const hasReachedBlurEllipse = useScrollTrigger('blur-ellipse', 100)

  return (
    <KnxToCrestronStepsStyled $alignCenter>
      <Container $column $mobileColumn $alignItems="center">
        {badge && (
          <div className="badge">
            <BodyXS $color={theme.textHighlight} $weight={500}>
              {badge}
            </BodyXS>
          </div>
        )}
        <TitleM className={`title ${subtitle ? 'mb-1' : 'mb-3'}`}>{intl.formatMessage({ id: 'block_knx_to_crestron_steps_title' })}</TitleM>
        {subtitle && <BodyM className="subtitle">{subtitle}</BodyM>}
        <motion.div
          id="content-1"
          className="content"
          initial={{ opacity: 0, y: 20 }}
          animate={hasReachedContent1 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >
          <div className="text-group">
            <TitleM $color={theme.textSecondary} $weight={700} className="number">
              1
            </TitleM>
            <TitleXXS className="item-title">{intl.formatMessage({ id: 'block_knx_to_crestron_steps_text_1_1' })}</TitleXXS>
            <BodyM className="item-text">{intl.formatMessage({ id: 'block_knx_to_crestron_steps_text_1_2' })}</BodyM>
          </div>
          <div className="img-group">
            <StaticImage
              className="img"
              src="https://www.1home.io/xassets/crestron-1.webp"
              alt={intl.formatMessage({ id: 'block_knx_to_crestron_steps_alt' })}
              placeholder="none"
              quality="100"
            />
          </div>
        </motion.div>
        <motion.div
          id="content-2"
          className="content"
          initial={{ opacity: 0, y: 20 }}
          animate={hasReachedContent2 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >
          <div className="text-group">
            <TitleM $color={theme.textSecondary} $weight={700} className="number">
              2
            </TitleM>
            <TitleXXS className="item-title">{intl.formatMessage({ id: 'block_knx_to_crestron_steps_text_2_1' })}</TitleXXS>
            <BodyM className="item-text">{intl.formatMessage({ id: 'block_knx_to_crestron_steps_text_2_2' })}</BodyM>
          </div>
          <div className="img-group">
            <StaticImage
              className="img"
              src="https://www.1home.io/xassets/crestron-2.webp"
              alt={intl.formatMessage({ id: 'block_knx_to_crestron_steps_alt' })}
              placeholder="none"
              quality="100"
            />
          </div>
        </motion.div>
        <motion.div
          id="content-3"
          className="content"
          initial={{ opacity: 0, y: 20 }}
          animate={hasReachedContent3 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >
          <div className="text-group">
            <TitleM $color={theme.textSecondary} $weight={700} className="number">
              3
            </TitleM>
            <TitleXXS className="item-title">{intl.formatMessage({ id: 'block_knx_to_crestron_steps_text_3_1' })}</TitleXXS>
            <BodyM className="item-text">{intl.formatMessage({ id: 'block_knx_to_crestron_steps_text_3_2' })}</BodyM>
          </div>
          <div className="img-group">
            <StaticImage
              className="img"
              src="https://www.1home.io/xassets/crestron-3.webp"
              alt={intl.formatMessage({ id: 'block_knx_to_crestron_steps_alt' })}
              placeholder="none"
              quality="100"
            />
          </div>
        </motion.div>
        {link}
      </Container>
      <motion.div
        id="blur-ellipse"
        className="blur-ellipse first"
        initial={{ opacity: 0 }}
        animate={hasReachedBlurEllipse ? { opacity: 0.25 } : { opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
      />
      <motion.div
        className="blur-ellipse second"
        initial={{ opacity: 0 }}
        animate={hasReachedBlurEllipse ? { opacity: 0.25 } : { opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
      />
      <GridBackground className="grid-overlay" />
    </KnxToCrestronStepsStyled>
  )
}

KnxToCrestronSteps.propTypes = {
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  link: PropTypes.node,
}

export default KnxToCrestronSteps
